import { useState, useEffect } from 'react';
import { Modal, TextInput, Select, Button, Loader } from '@mantine/core';
import { useAdminStore } from '@/store/admin';
import { editUser, createUser, updateUser } from '@mygpt/app';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import { useGPTStore } from '@/store';

interface MemberModalProps {
  onSubmit: (formData: any) => void;
  onClose: () => void;
  userId?: string;
  companyId: string;
  initialData?: any;
}

interface FormData {
  name: string;
  email: string;
  password?: string;
  sysRoleId?: number;
}

export default function MemberModal({ onSubmit, onClose, userId, companyId, initialData }: MemberModalProps) {
  const { companySetting, authorityRolesList } = useAdminStore();
  const { t } = useTranslation();
  const { roles } = useGPTStore((state) => state);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: companySetting?.initialPassword,
    sysRoleId: undefined,
    ...initialData,
  });
  const [loading, setLoading] = useState(false);

  // 获取当前用户的最高权限角色
  const getCurrentUserHighestRole = () => {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return null;
    }

    // 按权限等级排序：ROLE_COMPANY_OWNER > ROLE_ADMIN > ROLE_EDITOR > ROLE_VIEWER
    const roleHierarchy = {
      ROLE_COMPANY_OWNER: 4,
      ROLE_ADMIN: 3,
      ROLE_EDITOR: 2,
      ROLE_VIEWER: 1,
    };

    return roles.reduce((highest, current) => {
      if (!current || !highest) {
        return current || highest;
      }

      const currentLevel = roleHierarchy[current.code as keyof typeof roleHierarchy] || 0;
      const highestLevel = roleHierarchy[highest.code as keyof typeof roleHierarchy] || 0;
      return currentLevel > highestLevel ? current : highest;
    });
  };

  const currentUserRole = getCurrentUserHighestRole();

  // 根据当前用户权限过滤可选择的角色
  const getFilteredRoleOptions = () => {
    if (!authorityRolesList || !Array.isArray(authorityRolesList)) {
      return [];
    }

    if (!currentUserRole) {
      return [];
    }

    let allowedRoleCodes: string[] = [];

    switch (currentUserRole.code) {
      case 'ROLE_COMPANY_OWNER':
        // 公司所有者可以选择所有角色
        allowedRoleCodes = ['ROLE_COMPANY_OWNER', 'ROLE_ADMIN', 'ROLE_EDITOR', 'ROLE_VIEWER'];
        break;
      case 'ROLE_ADMIN':
        // 管理员只能选择编辑者和查看者
        allowedRoleCodes = ['ROLE_EDITOR', 'ROLE_VIEWER'];
        break;
      default:
        // 其他角色不能选择任何角色
        allowedRoleCodes = [];
        break;
    }

    return authorityRolesList
      .filter((role: any) => allowedRoleCodes.includes(role.code))
      .map((role: any) => ({
        value: role.id.toString(),
        label: role.code ? t(`adminConsole.employees.roles.${role.code}`) : role.name,
      }));
  };

  useEffect(() => {
    if (initialData) {
      // 如果有角色数据，获取第一个角色的ID作为sysRoleId
      const sysRoleId = initialData.roles && initialData.roles.length > 0 ? initialData.roles[0].id : undefined;
      setFormData({
        ...initialData,
        sysRoleId,
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev: FormData) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (name: string, value: string | null) => {
    setFormData((prev: FormData) => ({
      ...prev,
      [name]: value ? Number(value) : undefined,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 添加邮箱格式校验
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showNotification({
        message: t('adminConsole.tip.invalid_email'),
        color: 'red',
      });
      return;
    }

    if (!userId && !formData.sysRoleId) {
      showNotification({
        message: t('adminConsole.memberModal.selectRole'),
        color: 'red',
      });
      return;
    }

    setLoading(true);

    if (userId) {
      updateUser({
        id: userId,
        name: formData.name,
        email: formData.email,
        enabled: true, // 编辑时保持启用状态
        sysRoleId: formData.sysRoleId,
      })
        .then(({ data }) => {
          if (data.success) {
            showNotification({
              message: t('adminConsole.tip.update_success'),
              color: 'green',
            });
            onSubmit(formData);
          } else {
            showNotification({
              message: t('adminConsole.tip.update_error'),
              color: 'red',
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      createUser({
        name: formData.name,
        email: formData.email,
        companyId,
        password: companySetting?.initialPassword,
        sysRoleId: formData.sysRoleId,
      })
        .then(({ data }) => {
          if (data.success) {
            showNotification({
              message: t('adminConsole.tip.create_success'),
              color: 'green',
            });
            onSubmit(formData);
          } else {
            showNotification({
              message: t(`adminConsole.tip.${data.error.name}`),
              color: 'red',
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // 准备角色选项
  const roleOptions = getFilteredRoleOptions();

  return (
    <form onSubmit={handleSubmit}>
      <TextInput
        label={t('adminConsole.memberModal.name')}
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
        minLength={2}
        maxLength={50}
      />
      <TextInput
        label={t('adminConsole.memberModal.email')}
        name="email"
        value={formData.email}
        onChange={handleChange}
        required
        maxLength={50}
      />
      {!userId && (
        <Select
          label={t('adminConsole.memberModal.role')}
          placeholder={t('adminConsole.memberModal.selectRole')}
          data={roleOptions}
          value={formData.sysRoleId?.toString() || null}
          onChange={(value) => handleSelectChange('sysRoleId', value)}
          required
        />
      )}
      {userId && (
        <Select
          label={t('adminConsole.memberModal.role')}
          placeholder={t('adminConsole.memberModal.selectRole')}
          data={roleOptions}
          value={formData.sysRoleId?.toString() || null}
          onChange={(value) => handleSelectChange('sysRoleId', value)}
        />
      )}
      {companySetting?.initialPassword && !userId && (
        <TextInput
          label={t('adminConsole.memberModal.password')}
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
          readOnly
          classNames={{
            input: 'bg-gray-100 text-gray-500 cursor-not-allowed', // 确保样式应用到输入框
          }}
        />
      )}
      <div className="flex justify-end gap-3 mt-6">
        <Button variant="outline" onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={loading} leftSection={loading ? <Loader size="xs" /> : null}>
          {loading ? t('common.submitting') : userId ? t('common.update') : t('common.create')}
        </Button>
      </div>
    </form>
  );
}
